import moment from 'moment';
import React from 'react';
import { DataGridPro, GridToolbarExport, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton } from '@mui/x-data-grid-pro';
import { Chip } from '@mui/material';
import { useParams } from 'react-router-dom';
import * as CustomerApi from '../../../Services/CustomerApi'

export default function SubscriptionHistory({profile}) {
    const { customerId } = useParams();
    const [historyList, setHistoryList] = React.useState([])

    function renderAction(action) {
        switch(action) {
          case 'Subscribed':
            return <Chip label={action} color="success" />;
          case 'Renewed':
            return <Chip variant="outlined" label={action} color="success" />;
          case 'Cancelled':
            return <Chip label={action} color="error" />;
          case 'Note was added': 
          case 'Note was updated':
          case 'Cancellation note was added':
          case 'Cancellation note was updated':
            return <Chip variant="outlined" label={action} />;
          default:
            return '';
        }
      }

    const columns = [
        { 
            field: 'id', 
            headerName: 'id', 
            width: 100, 
            editable: false ,
            hide: true,
        },
        { 
            field: 'action', 
            headerName: 'Action', 
            type: 'singleSelect',
            width: 200, 
            editable: false ,
            renderCell: (params) => {
                return renderAction(params.value);
              },
            valueOptions:['Subscribed', 'Renewed', 'Cancelled', 'Note was added', 'Note was updated', 'Cancellation note was added', 'Cancellation note was updated']
        },        
        {
            field: 'subscriptionTier',
            headerName: 'Subscription tier',
            width: 150, 
            editable: false,
        },
        {
            field: 'subscription',
            headerName: 'Subscription name',
            width: 300, 
            editable: false,
        },
        {
            field: 'actionBy',
            headerName: 'Actioned by',
            width: 150, 
            editable: false,
        },
        {
            field: 'actionUTC',
            headerName: 'Actioned on (UTC)',
            type: 'date',
            width: 150, 
            valueFormatter: (params) => {
                return params.value ? moment(params.value).utc().format('DD MMM YYYY hh:mm a') : "";
            },
            valueGetter: (params) => {
                return params.value ? moment(params.value) : "";
            },
            editable: false,
        },
        {
            field: 'actionLocal',
            headerName: 'Actioned on (Subscription Timezone)',
            width: 150, 
            editable: false,
        },
        {
            field: 'detail',
            headerName: 'Detail',
            width: 250, 
            editable: false,
        },
        {
            field: 'notes',
            headerName: 'Note',
            width: 250, 
            editable: false,
        },
    ];

    const fetchSubscriptionData = React.useCallback(async () => {
        const data = await CustomerApi.getCustomerSubscriptionHistory(customerId);
        const mappedList = [];
        var id = 0;

        // For all the subscriptions user had
        data.forEach((subscription) => {
            
            const payments = subscription?.payments.sort((a,b)=>a.paymentCreatedOn > b.paymentCreatedOn ? 1 : -1); 
            const timezone = subscription?.subscriptionType?.timezone ? subscription?.subscriptionType?.timezone : 'UTC';            
            const subscriptionNotes = subscription?.notes.sort((a,b)=>b.notesUpdatedOn > a.notesUpdatedOn ? 1 : -1);
            const addNotes = subscriptionNotes?.filter(note=>note.type === 'ADD');
            const cancelNotes = subscriptionNotes?.filter(note=>note.type === 'CANCEL');
            var tempDate = moment([2022, 6, 19]).utc(); //temp date to hide wrong older renewal dates AMZG-3047

            // For all payments of specific subscription including renewals
            payments.forEach((payment, k) => {

                // Subscription start or renew
                mappedList.push({
                    "action": k===0 ? "Subscribed" : "Renewed", 
                    "subscription": subscription?.subscriptionType?.name,
                    "subscriptionTier": subscription?.subscriptionType?.tier,
                    "detail": k===0 ? `Subscription start date = ${moment(subscription?.startDate).utc().tz(timezone).format('DD MMM YYYY')}` : (moment(payment?.paymentCreatedOn).utc().diff(tempDate, 'days') > 0 ? `Next billing date = ${moment(payment?.validityEndDate).utc().tz(timezone).add(1, 'days').format('DD MMM YYYY')}` : ""),
                    "notes": payment?.notes,
                    "actionUTC": payment?.paymentCreatedOn,
                    "actionLocal": `${moment(payment?.paymentCreatedOn).utc().tz(timezone).format('DD MMM YYYY hh:mm a')} ${moment.tz(timezone).zoneAbbr()}`,
                    "actionBy": payment.paymentCreatedBy,
                    "actionUTCMoment": moment(payment?.paymentCreatedOn).utc(),
                    "id": id,
                });
                id = id + 1;

                // Add row in history if subscription cancel
                if(k===payments.length-1 && subscription?.status==="CANCELLED"){                   
                    mappedList.push({
                        "action": "Cancelled", 
                        "subscription": subscription?.subscriptionType.name,
                        "subscriptionTier": subscription?.subscriptionType?.tier,
                        "detail": `Subscription end date = ${moment(subscription?.endDate).utc().tz(timezone).format('DD MMM YYYY')}`,
                        "notes": cancelNotes[0]?.notes,
                        "actionUTC": subscription?.updatedOn,
                        "actionLocal": `${moment(subscription?.updatedOn).utc().tz(timezone).format('DD MMM YYYY hh:mm a')} ${moment.tz(timezone).zoneAbbr()}`,
                        "actionBy": subscription?.updatedBy,
                        "actionUTCMoment": moment(subscription?.updatedOn).utc(),
                        "id": id,
                    });
                    id = id + 1;
                }

            }); 

            addNotes.forEach((note, i) => {

                // Note added/updated after the subscription created
                if(note?.notesCreatedOn !== subscription?.createdOn ){                   
                    mappedList.push({
                        "action": i===addNotes.length-1 ? "Note was added" : "Note was updated", 
                        "subscription": subscription?.subscriptionType.name,
                        "subscriptionTier": subscription?.subscriptionType?.tier,
                        "detail": i===addNotes.length-1 ? "" : `Previous note = ${addNotes[i+1]?.notes}`,
                        "notes": `Updated note = ${note?.notes}`,
                        "actionUTC": note?.notesUpdatedOn,
                        "actionLocal": `${moment(note?.notesUpdatedOn).utc().tz(timezone).format('DD MMM YYYY hh:mm a')} ${moment.tz(timezone).zoneAbbr()}`,
                        "actionBy": note?.notesUpdatedBy,
                        "actionUTCMoment": moment(note?.notesUpdatedOn).utc(),
                        "id": id,
                    });
                    id = id + 1;
                }
            });

            cancelNotes.forEach((note, i) => {

                // Note added/updated after the subscription cancelled
                if(note?.notesCreatedOn !== subscription?.updatedOn ){                   
                    mappedList.push({
                        "action": i===cancelNotes.length-1 ? "Cancellation note was added" : "Cancellation note was updated", 
                        "subscription": subscription?.subscriptionType.name,
                        "subscriptionTier": subscription?.subscriptionType?.tier,
                        "detail": i===cancelNotes.length-1 ? "" : `Previous note = ${cancelNotes[i+1]?.notes}`,
                        "notes": `Updated note = ${note?.notes}`,
                        "actionUTC": note?.notesUpdatedOn,
                        "actionLocal": `${moment(note?.notesUpdatedOn).utc().tz(timezone).format('DD MMM YYYY hh:mm a')} ${moment.tz(timezone).zoneAbbr()}`,
                        "actionBy": note?.notesUpdatedBy,
                        "actionUTCMoment": moment(note?.notesUpdatedOn).utc(),
                        "id": id,
                    });
                    id = id + 1;
                }
            });
        });        

        setHistoryList(mappedList);

    }, [customerId]);

    React.useEffect(() => {
        fetchSubscriptionData();
    }, [fetchSubscriptionData])    

    return (
        historyList?.length > 0 ?
            <DataGridPro
                style={{ flex: 1 }}
                rows={[...historyList].sort((a,b)=>b.actionUTCMoment > a.actionUTCMoment ? 1 : -1)}
                columns={columns}
                pagination={true}
                pageSize={50}
                sx={{
                    '.MuiDataGrid-columnHeaders': {
                      fontWeight: 'bold',
                      fontSize: 14,
                      color: "rgb(0,0,0)",
                      backgroundColor: "rgb(244,246,248)"
                    },
                    '.MuiTablePagination-selectLabel': {
                      marginBottom: '0px',
                    },
                    '.MuiTablePagination-displayedRows': {
                      marginBottom: '0px',
                    },
                    '.MuiDataGrid-main': {
                      height: 'calc(100vh - 160px)',
                    },
                  }}
                components={{ Toolbar: dataGridToolbar }}
                componentsProps={{
                    panel: {
                      sx: {
                        '& .MuiTypography-root': {
                            color: '#000',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#D1517A',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
                            backgroundColor: '#D1517A',
                        },                          
                        '& .MuiFormLabel-root': {
                            color: '#D1517A',
                        },
                        '& .MuiButton-root': {
                            color: '#D1517A',
                        },
                        '& .MuiInputLabel-root': {
                            color: '#D1517A',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: '#D1517A',
                        },
                        '& .MuiInput-root:after': {
                            borderBottom: '1px solid #000',
                        },
                      },
                    },
                  }}
            />
            :
            <span style={{fontSize: '16px', paddingLeft: '15px'}}>{profile?.firstName + ' ' + profile?.lastName} is not yet a paid WonderPass subscriber</span>  
        
    );

    function dataGridToolbar() {
        return (
          <GridToolbarContainer style={{paddingTop: '10px'}}>
            <GridToolbarColumnsButton style={{color: '#434F69'}} />
            <GridToolbarFilterButton style={{color: '#434F69'}}/>
            <GridToolbarExport style={{color: '#434F69'}}/>
          </GridToolbarContainer>
        );
    }
}
